import {
  TellescopeModelFromName,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { ConfirmDialog, DashboardCard } from "@tellescope/ui";
import { formatDateTime } from "@tellescope/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface ContentSectionProps {
  contact: TellescopeModelFromName<"endusers">;
}

export const ContentSection = ({ contact }: ContentSectionProps) => {
  const { t } = useTranslation("content");
  const [contentAssignmentToDelete, setContentAssignmentToDelete] = useState<string | null>(null);

  // Query for managed content record assignments for this enduser
  const assignmentsQuery = useTellescopeInfiniteQuery({
    model: "managed_content_record_assignments",
    method: "getSome",
    request: {
      filter: {
        enduserId: contact.id,
      },
    },
    select: (data) => {
      const assignments = data.pages.flat();
      return new Map(assignments.map((assignment) => [assignment.contentId, assignment]));
    },
  });

  // Extract contentIds from the assignmentsMap keys
  const contentIds = assignmentsQuery.data ? Array.from(assignmentsQuery.data.keys()) : [];

  // Query for the actual managed content records
  const contentQuery = useTellescopeQuery({
    model: "managed_content_records",
    method: "getByIds",
    request: {
      ids: contentIds,
    },
    enabled: contentIds.length > 0,
  });

  const deleteContentAssignmentMutation = useTellescopeMutation({
    model: "managed_content_record_assignments",
    method: "deleteOne",

    onSuccess: () => {
      setContentAssignmentToDelete(null);
    },
  });

  const assignmentsMap = assignmentsQuery.data ?? new Map();
  const contentRecords = contentQuery.data?.matches ?? [];

  if (assignmentsQuery.isLoading || contentQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">{t("Loading content...")}</div>
        </div>
      </div>
    );
  }

  if (!assignmentsQuery.data?.size) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">{t("No content found")}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto space-y-4 pr-2">
        {contentRecords.map((content) => {
          const assignment = assignmentsMap.get(content.id);
          if (!assignment) return null; // Should never happen since we query by contentIds from assignments
          return (
            <DashboardCard
              key={content.id}
              title={content.title}
              statusLabel=""
              subtitle={content.type || ""}
              subtitleSecondary={content.assignmentType || ""}
              description={content.description || ""}
              eventDate={formatDateTime(assignment.updatedAt, "MM/DD/YYYY at hh:mm a", true)}
              assignees={[]}
              onDelete={() => setContentAssignmentToDelete(assignment.id)}
            />
          );
        })}
      </div>

      <ConfirmDialog
        open={!!contentAssignmentToDelete}
        onOpenChange={(open) => !open && setContentAssignmentToDelete(null)}
        variant="destructive"
        title={t("Delete Content Assignment")}
        description={t("Are you sure you want to delete this content assignment? This action cannot be undone.")}
        confirmText={t("Delete")}
        onConfirm={async () => {
          if (contentAssignmentToDelete) {
            await deleteContentAssignmentMutation.mutateAsync(contentAssignmentToDelete);
          }
        }}
      />
    </>
  );
};
